import { initContract } from '@ts-rest/core';
import { z } from 'zod';

// Shared optional auth header schema for all protected endpoints

const StreamingHeadersSchema = z
  .object({
    'x-stream': z.literal('true'),
  })
  .default({ 'x-stream': 'true' });

import { User } from '..';
import { AiSkillPublicSchema } from '../ai-skills/types';
import { EnhancedChatMessageSchema } from '../agentic/common';
import {
  CreateCheckoutSessionInputSchema,
  CreateCheckoutSessionOutputSchema,
  CreatePortalSessionInputSchema,
  CreatePortalSessionOutputSchema,
  GetUserSubscriptionOutputSchema,
  SubscriptionPlanSchema,
  UpdateSubscriptionInputSchema,
  UpdateSubscriptionOutputSchema,
} from '../billing';
import { SubmitBugReportInputSchema, SubmitBugReportOutputSchema } from '../bug-report';
import {
  CloseTaskInputSchema,
  EnterTaskSchema,
  ClaimSummarySchema,
  PostTaskCommentInputSchema,
  TaskCommentSchema,
} from '../integrations/enter-tasks';
import {
  PaymentListItemSchema,
  RecordPaymentInputSchema,
  RecordedPaymentSchema,
  RefundPaymentInputSchema,
} from '../integrations/enter-payments';
import {
  ArchiveChatOutputSchema,
  ChatFileUploadInputSchema,
  ChatFileUploadOutputSchema,
  CreateChatSessionInputSchema,
  CreateChatSessionOutputSchema,
  DeleteChatOutputSchema,
  GetChatHistoryOutputSchema,
  GetUserChatsOutputSchema,
  JSONExportInputSchema,
  JSONExportOutputSchema,
  JSONImportInputSchema,
  JSONImportOutputSchema,
  ListJSONExportsOutputSchema,
  SaveChatMessageInputSchema,
  SaveChatMessageOutputSchema,
  UpdateChatTitleInputSchema,
  UpdateChatTitleOutputSchema,
} from '../chat-history';
import {
  DismissFeaturesInputSchema,
  GetFeaturesOutputSchema,
  SubmitFeedbackInputSchema,
  SubmitFeedbackOutputSchema,
} from '../feedback';
import { TTSSynthesisRequestSchema, TTSSynthesisResponseSchema } from '../openai-tts';
import {
  GetOrgPersonalizationOutputSchema,
  GetUserPersonalizationOutputSchema,
  UpdateOrgPersonalizationInputSchema,
  UpdateOrgPersonalizationOutputSchema,
  UpdateUserPersonalizationInputSchema,
  UpdateUserPersonalizationOutputSchema,
} from '../personalization';
import {
  CreatePromptInputSchema,
  CreatePromptOutputSchema,
  DeletePromptOutputSchema,
  GenerateSuggestionsInputSchema,
  GenerateSuggestionsOutputSchema,
  GetOrgPromptsOutputSchema,
  GetUserPreferencesOutputSchema,
  UpdatePromptInputSchema,
  UpdatePromptOutputSchema,
  UpdateUserPreferencesInputSchema,
  UpdateUserPreferencesOutputSchema,
} from '../prompt-templates';

import { STTTranscriptionRequestSchema, STTTranscriptionResponseSchema } from '../openai-stt';
import {
  ResolveRealTimeModeInputSchema,
  ResolveRealTimeModeOutputSchema,
} from '../integrations/types';

// PandaDoc schemas
const BAASigningSessionSchema = z.object({
  documentId: z.string(),
  sessionUrl: z.string(), // Still needed for external browser opening
  expiresAt: z.string(),
  mode: z.enum(['signing', 'viewing']).optional(), // Indicates whether user is signing or viewing
});

const BAAStatusSchema = z.object({
  documentId: z.string(),
  status: z.enum(['draft', 'sent', 'completed', 'cancelled', 'declined', 'signed']),
  isSigned: z.boolean(),
  completedAt: z.string().optional(),
  signedBy: z.array(
    z.object({
      email: z.string(),
      signedAt: z.string(),
    })
  ),
});

const UserBAAStatusSchema = z.object({
  hasSigned: z.boolean(),
  signedAt: z.string().optional(),
});

const DocumentRecipientSchema = z.object({
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  role: z.string().optional(),
  signing_order: z.number().optional(),
  has_completed: z.boolean().optional(),
  date_completed: z.string().optional(),
});

const DocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  date_created: z.string(),
  date_modified: z.string(),
  expiration_date: z.string().optional(),
  version: z.string(),
  uuid: z.string(),
  template: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  folder: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  recipients: z.array(DocumentRecipientSchema),
  tokens: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
      })
    )
    .optional(),
  fields: z
    .array(
      z.object({
        uuid: z.string(),
        name: z.string(),
        title: z.string(),
        value: z.string(),
        assigned_to: z.string().optional(),
      })
    )
    .optional(),
  metadata: z.record(z.any()).optional(),
  tags: z.array(z.string()).optional(),
  date_completed: z.string().optional(),
  shared_link: z.string().optional(),
});

const c = initContract();

// Health check contract
export const healthContract = c.router({
  health: {
    method: 'GET',
    path: '/health',
    responses: {
      200: z.object({
        status: z.string(),
        timestamp: z.string(),
        service: z.string(),
      }),
    },
    summary: 'Health check endpoint',
  },
});

export const webhookContract = c.router({
  handleWorkOSWebhook: {
    method: 'POST',
    path: '/webhook/workos/membership',
    body: z.any().describe('Raw webhook payload as buffer that will be parsed in the handler'),
    responses: {
      200: z.object({
        success: z.boolean(),
        message: z.string(),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Handle WorkOS webhook events for organization membership changes',
  },
});

// Authentication contract
export const authContract = c.router({
  login: {
    method: 'GET',
    path: '/auth/login',
    query: z.object({
      platform: z.string().optional(),
      // Optional post-login destination (internal path) — carried through `state`.
      returnTo: z.string().optional(),
    }),
    responses: {
      200: c.type<{
        redirectUrl: string;
      }>(),
      500: c.type<{
        error: string;
      }>(),
    },
  },
  callback: {
    method: 'GET',
    path: '/auth/callback',
    query: z.object({
      code: z.string().optional(),
      // OAuth state (JSON string) carrying deviceId and/or a post-login returnTo path.
      state: z.string().optional(),
      error: z.string().optional(),
      error_description: z.string().optional(),
    }),
    responses: {
      200: c.type<{
        user: User;
        accessToken: string;
        refreshToken: string;
        expiresAt: number;
        message: string;
      }>(),
      500: c.type<{
        error: string;
      }>(),
    },
  },
  me: {
    method: 'GET',
    path: '/auth/me',
    responses: {
      200: c.type<{
        user: User;
      }>(),
      401: c.type<{
        error: string;
        message: string;
      }>(),
      403: c.type<{
        error: string;
        message: string;
      }>(),
    },
  },
  logout: {
    method: 'POST',
    path: '/auth/logout',
    body: z.object({
      userId: z.string(),
    }),
    responses: {
      200: c.type<{
        success: boolean;
      }>(),
      500: c.type<{
        error: string;
      }>(),
    },
  },
  refresh: {
    method: 'POST',
    path: '/auth/refresh',
    body: z.object({
      refreshToken: z.string(),
    }),
    responses: {
      200: c.type<{
        accessToken: string;
        refreshToken: string;
        user: User;
        expiresAt: number;
        message: string;
      }>(),
      401: c.type<{
        error: string;
        message: string;
      }>(),
      500: c.type<{
        error: string;
      }>(),
    },
  },
  widget: {
    method: 'GET',
    path: '/auth/widget',
    responses: {
      200: c.type<{
        widgetToken: string;
      }>(),
      401: c.type<{
        error: string;
        message: string;
      }>(),
      403: c.type<{
        error: string;
        message: string;
      }>(),
      500: c.type<{
        error: string;
      }>(),
    },
  },
  refreshUser: {
    method: 'POST',
    path: '/auth/refresh-user',
    body: z.object({
      refreshToken: z
        .string()
        .describe('Refresh token to exchange for new tokens with updated role claims'),
    }),
    responses: {
      200: c.type<{
        user: User;
        message: string;
        accessToken: string;
        refreshToken: string;
        expiresAt: number;
      }>(),
      400: c.type<{
        error: string;
        message: string;
      }>(),
      401: c.type<{
        error: string;
        message: string;
      }>(),
      500: c.type<{
        error: string;
        message: string;
      }>(),
    },
    summary:
      'Refresh user data including role and permissions, and issue new JWT tokens with updated role claims from WorkOS',
  },
  resetPassword: {
    method: 'GET',
    path: '/auth/reset-password',
    query: z.object({
      token: z.string().describe('Password reset token from WorkOS'),
    }),
    responses: {
      200: c.type<{
        resetUrl: string;
        message: string;
      }>(),
      400: c.type<{
        error: string;
        message: string;
      }>(),
      500: c.type<{
        error: string;
        message: string;
      }>(),
    },
    summary: 'Handle password reset token and redirect to WorkOS password reset interface',
  },
  submitPasswordReset: {
    method: 'POST',
    path: '/auth/submit-password-reset',
    body: z.object({
      token: z.string().describe('Password reset token from WorkOS'),
      newPassword: z.string().min(8).describe('New password (minimum 8 characters)'),
    }),
    responses: {
      200: c.type<{
        success: boolean;
        message: string;
      }>(),
      400: c.type<{
        error: string;
        message: string;
      }>(),
      500: c.type<{
        error: string;
        message: string;
      }>(),
    },
    summary: 'Submit new password using WorkOS password reset token',
  },
});

// Base64 image attachment schema (deprecated - use S3ImageSchema)
export const Base64ImageSchema = z.object({
  base64: z.string().describe('Base64 encoded image data'),
  mimeType: z.string().describe('MIME type of the image (e.g., image/png, image/jpeg)'),
  filename: z.string().optional().describe('Original filename'),
  isScreenshot: z.boolean().default(false).describe('Whether this is a screenshot'),
});

// S3 image attachment schema (preferred for images)
export const S3ImageSchema = z.object({
  s3_key: z.string().describe('S3 file key where image is stored'),
  url: z.string().describe('Presigned URL for accessing the image'),
  mimeType: z.string().describe('MIME type of the image (e.g., image/png, image/jpeg)'),
  filename: z.string().optional().describe('Original filename'),
  isScreenshot: z.boolean().default(false).describe('Whether this is a screenshot'),
});

// S3 file attachment schema (for documents like PDFs)
export const S3FileSchema = z.object({
  s3_key: z.string().describe('S3 file key where file is stored'),
  url: z.string().describe('Presigned URL for accessing the file'),
  mimeType: z.string().describe('MIME type of the file (e.g., application/pdf, text/plain)'),
  filename: z.string().optional().describe('Original filename'),
  isScreenshot: z.boolean().default(false).describe('Whether this is a screenshot'),
});

// Unified image schema that supports both base64 and S3 (images only)
export const ImageAttachmentSchema = z.union([Base64ImageSchema, S3ImageSchema]);

// Unified file schema that supports both images and documents
export const FileAttachmentSchema = z.union([
  Base64ImageSchema, // For base64 images
  S3ImageSchema, // For S3 images
  S3FileSchema, // For S3 files (PDFs, etc.)
]);

export type Base64Image = z.infer<typeof Base64ImageSchema>;
export type S3Image = z.infer<typeof S3ImageSchema>;
export type S3File = z.infer<typeof S3FileSchema>;
export type ImageAttachment = z.infer<typeof ImageAttachmentSchema>;
export type FileAttachment = z.infer<typeof FileAttachmentSchema>;

// Enhanced AI request body schema
export const AskExpertInputSchema = z.object({
  userInput: z.string().min(1),
  chatHistory: z.array(EnhancedChatMessageSchema).optional().default([]),
  additionalContext: z.string().optional(),
  screenshots: z
    .array(ImageAttachmentSchema)
    .optional()
    .describe('Screenshots (supports both base64 and S3 URLs for images only)'),
  attachments: z
    .array(FileAttachmentSchema)
    .optional()
    .describe('File attachments (supports images and documents like PDFs via S3 URLs)'),
  sessionId: z.string().optional().describe('Optional session ID to save messages to chat history'),
  pageContextMeta: z
    .object({
      title: z.string(),
      url: z.string(),
      charCount: z.number().int(),
      wasTruncated: z.boolean(),
    })
    .optional()
    .describe('Metadata for a captured web page — stored with the message, not sent to AI'),
  selectedMcpAccountId: z
    .string()
    .optional()
    .describe('Optional Enter Health account ID for MCP integration'),
  personalization: z
    .object({
      activeMode: z.enum(['org', 'custom']),
      userSettings: z.object({
        displayName: z.string().optional(),
        jobTitle: z.string().optional(),
        specialtyOverride: z.string().optional(),
        additionalCptCodes: z.array(z.string()).optional(),
        customInstructions: z.string().optional(),
        otherDetails: z.string().optional(),
      }),
    })
    .optional()
    .describe('User personalization settings for AI context'),
  compactionToken: z
    .string()
    .optional()
    .describe('Encrypted compaction token from a prior /ai/compact-context call'),
  compactionMessageIndex: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe('Index into chatHistory from which new messages start after compaction'),
  aiSkillId: z
    .string()
    .optional()
    .describe('ID of the active AI specialist skill to inject into the prompt context'),
});

// File metadata type
export const FileMetadataSchema = z.object({
  isScreenshot: z.boolean().default(false),
  filename: z.string().optional(),
  description: z.string().optional(),
  sessionId: z.string().optional().describe('Chat session ID for organizing uploads'),
});

export type FileMetadata = z.infer<typeof FileMetadataSchema>;

export type AskExpertInput = z.infer<typeof AskExpertInputSchema>;

// Multipart file type that works for both client and server
export type MultipartFile = File | Buffer | { filename: string; mimetype: string; value: Buffer };

// File Upload contract (public + user routes) - now returns base64 data
export const fileUploadContract = c.router({
  convertToBase64: {
    method: 'POST',
    path: '/ai/upload-file',
    contentType: 'multipart/form-data',
    body: c.type<{
      file: MultipartFile;
      metadata?: string; // JSON string of FileMetadata
    }>(),
    responses: {
      200: z.object({
        s3_key: z.string().describe('S3 file key where the file is stored'),
        presigned_url: z.string().describe('Presigned URL for immediate access (24h expiry)'),
        filename: z.string().describe('Generated unique filename'),
        original_filename: z.string().describe('Original uploaded filename'),
        size: z.number().describe('File size in bytes'),
        mimeType: z.string().describe('File MIME type'),
        isScreenshot: z.boolean().describe('Whether this is a screenshot'),
        // Deprecated: keeping for backward compatibility
        base64: z.string().describe('Empty - deprecated, use S3 URLs instead'),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary:
      'Upload a file to OpenAI and return the file ID for vision and document processing tasks',
  },
});

// Ask Expert contract (public + user routes)
export const aiContract = c.router({
  askExpert: {
    method: 'POST',
    path: '/ai/ask-expert',
    contentType: 'application/json',
    headers: StreamingHeadersSchema,
    body: AskExpertInputSchema,
    responses: {
      200: c.type<ReadableStream>(), // Streaming response
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Enhanced AI chat endpoint with vision using base64 encoded images',
  },
  generateTitle: {
    method: 'POST',
    path: '/ai/generate-title',
    body: z.object({
      firstMessage: z.string().min(1).describe('The first message in the chat session'),
    }),
    responses: {
      200: z.object({
        title: z.string().describe('Generated title for the chat session'),
        generated: z
          .boolean()
          .describe('Whether the title was successfully generated or is a fallback'),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Generate concise titles for chat sessions',
  },
  generateSuggestions: {
    method: 'POST',
    path: '/ai/generate-suggestions',
    body: GenerateSuggestionsInputSchema,
    responses: {
      200: GenerateSuggestionsOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary:
      'Generate AI-powered prompt suggestions based on context (screen captures, chat history)',
  },
  compactContext: {
    method: 'POST',
    path: '/ai/compact-context',
    body: z.object({
      chatHistory: z.array(EnhancedChatMessageSchema),
      sessionId: z.string().optional(),
    }),
    responses: {
      200: z.object({
        compactionToken: z
          .string()
          .describe(
            'Encrypted compaction content from OpenAI — pass back in future askExpert calls'
          ),
        compactionMessageIndex: z
          .number()
          .describe(
            'Number of messages that were compacted — slice chatHistory from this index for future calls'
          ),
      }),
      400: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Compact conversation context using the OpenAI Responses Compact API',
  },
});

// Billing contract (protected routes)
export const billingContract = c.router({
  getUserSubscription: {
    method: 'GET',
    path: '/billing/subscription',
    responses: {
      200: GetUserSubscriptionOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get user subscription and available plans',
  },
  updateSubscription: {
    method: 'POST',
    path: '/billing/subscription',
    body: UpdateSubscriptionInputSchema,
    responses: {
      200: UpdateSubscriptionOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Create, update, or cancel user subscription',
  },
  createCheckoutSession: {
    method: 'POST',
    path: '/billing/checkout',
    body: CreateCheckoutSessionInputSchema,
    responses: {
      200: CreateCheckoutSessionOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: "Create a checkout session for the logged in WorkOS organization's stripe customer",
  },
  createPortalSession: {
    method: 'POST',
    path: '/billing/portal_session',
    body: CreatePortalSessionInputSchema,
    responses: {
      200: CreatePortalSessionOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary:
      "Create a Stripe billing portal session for the logged in WorkOS organization's stripe customer",
  },
  checkChatAccess: {
    method: 'GET',
    path: '/billing/chat-access',
    responses: {
      200: z.object({
        hasAccess: z.boolean(),
        error: z.string().optional(),
        requirements: z.array(z.string()),
        subscription: z
          .object({
            status: z.enum(['active', 'inactive', 'pending', 'processing', 'unknown']),
            billingType: z.enum(['manual', 'stripe']).optional(),
            hasEntitlement: z.boolean(),
            hasBaa: z.boolean(),
            seatCount: z.number().optional(),
            stripeCustomerId: z.string().optional(),
            organizationName: z.string().optional(),
            lastChecked: z.string(), // ISO timestamp
          })
          .optional(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Check if user has chat access based on organization subscription quantity',
  },
  checkRecentPayment: {
    method: 'GET',
    path: '/billing/check-recent-payment',
    responses: {
      200: z.object({
        paymentCompleted: z.boolean(),
        sessionId: z.string().optional(),
        paymentStatus: z.string().optional(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Check if user recently completed a payment (within last 5 minutes)',
  },
});

// Plans contract (public routes)
export const plansContract = c.router({
  getPlans: {
    method: 'GET',
    path: '/plans',
    query: z.object({
      source: z.string().optional(),
    }),
    responses: {
      200: z.object({
        plans: z.array(SubscriptionPlanSchema),
        success: z.boolean(),
        error: z.string().optional(),
      }),
    },
    summary: 'Get all available subscription plans (public endpoint)',
  },
});

// PandaDoc contract (protected routes)
export const pandaDocContract = c.router({
  createBAASigningSession: {
    method: 'POST',
    path: '/agreements/baa/create',
    body: z.object({
      viewIfSigned: z.boolean().optional(), // If true, return viewing session for already-signed documents
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        data: BAASigningSessionSchema,
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Create a new BAA signing session or viewing session if already signed',
  },
  getBAAStatus: {
    method: 'GET',
    path: '/agreements/baa/status/:documentId',
    pathParams: z.object({
      documentId: z.string(),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        data: BAAStatusSchema,
      }),
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get BAA document status',
  },
  checkUserBAAStatus: {
    method: 'POST',
    path: '/agreements/baa/check-user-status',
    body: z.object({
      documentId: z.string(),
      userEmail: z.string().email(),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        data: UserBAAStatusSchema,
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Check if user has signed BAA',
  },
  getDocumentById: {
    method: 'GET',
    path: '/documents/:documentId',
    pathParams: z.object({
      documentId: z.string(),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        data: DocumentSchema,
      }),
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get document by ID from PandaDoc',
  },
  baaWebhook: {
    method: 'POST',
    path: '/agreements/baa/webhook',
    query: z
      .object({
        signature: z.string().optional(),
      })
      .optional(),
    headers: z
      .object({
        'x-pandadoc-signature': z.string().optional(),
      })
      .optional(),
    body: z.array(
      z.object({
        event: z.string(),
        data: z.record(z.any()),
      })
    ),
    responses: {
      200: z.object({
        success: z.boolean(),
        message: z.string(),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Handle PandaDoc webhook events with signature verification',
  },
});

// Chat History contract (protected routes)
export const chatHistoryContract = c.router({
  getUserChats: {
    method: 'GET',
    path: '/chat-history/chats',
    responses: {
      200: GetUserChatsOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get all chat sessions for the authenticated user',
  },
  getChatHistory: {
    method: 'GET',
    path: '/chat-history/:sessionId',
    pathParams: z.object({
      sessionId: z.string(),
    }),
    responses: {
      200: GetChatHistoryOutputSchema,
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get a specific chat session history',
  },
  createChatSession: {
    method: 'POST',
    path: '/chat-history/create',
    body: CreateChatSessionInputSchema,
    responses: {
      201: CreateChatSessionOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Create a new chat session',
  },
  saveChatMessage: {
    method: 'POST',
    path: '/chat-history/message',
    body: SaveChatMessageInputSchema,
    responses: {
      200: SaveChatMessageOutputSchema,
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Save a message to an existing chat session',
  },
  deleteChat: {
    method: 'DELETE',
    path: '/chat-history/:sessionId',
    pathParams: z.object({
      sessionId: z.string(),
    }),
    responses: {
      200: DeleteChatOutputSchema,
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Delete a chat session and all its data',
  },
  archiveChat: {
    method: 'PATCH',
    path: '/chat-history/:sessionId/archive',
    pathParams: z.object({
      sessionId: z.string(),
    }),
    body: z.object({}), // Empty body for PATCH
    responses: {
      200: ArchiveChatOutputSchema,
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Archive a chat session',
  },
  unarchiveChat: {
    method: 'PATCH',
    path: '/chat-history/:sessionId/unarchive',
    pathParams: z.object({
      sessionId: z.string(),
    }),
    body: z.object({}), // Empty body for PATCH
    responses: {
      200: ArchiveChatOutputSchema,
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Unarchive a chat session',
  },
  updateChatTitle: {
    method: 'PATCH',
    path: '/chat-history/:sessionId/title',
    pathParams: z.object({
      sessionId: z.string(),
    }),
    body: UpdateChatTitleInputSchema,
    responses: {
      200: UpdateChatTitleOutputSchema,
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Update a chat session title',
  },
  uploadChatFile: {
    method: 'POST',
    path: '/chat-history/upload',
    body: ChatFileUploadInputSchema,
    responses: {
      200: ChatFileUploadOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Upload a file to be attached to a chat session',
  },
  exportChatJSON: {
    method: 'POST',
    path: '/chat-history/export/json',
    body: JSONExportInputSchema,
    responses: {
      200: JSONExportOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Export chat history as JSON file to S3 with presigned download URL',
  },
  importChatJSON: {
    method: 'POST',
    path: '/chat-history/import/json',
    body: JSONImportInputSchema,
    responses: {
      200: JSONImportOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Import chat history from JSON data',
  },
  listJSONExports: {
    method: 'GET',
    path: '/chat-history/exports',
    responses: {
      200: ListJSONExportsOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'List all JSON export files for the authenticated user',
  },
  deleteJSONExport: {
    method: 'DELETE',
    path: '/chat-history/exports/:fileName',
    pathParams: z.object({
      fileName: z.string(),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Delete a JSON export file',
  },
});

// Integration data schema (for responses)
// api_key and secret_key are optional for non-admin users (filtered out for security)
const IntegrationDataSchema = z.object({
  api_key: z.string().optional(),
  secret_key: z.string().optional(),
  business_name: z.string(),
  account_id: z.string(),
  role_id: z.string(),
  logo_url: z.string().nullable().optional(),
});

// Integration save schema (for saving - requires credentials)
const SaveIntegrationSchema = z.object({
  api_key: z.string(),
  secret_key: z.string(),
  business_name: z.string(),
  account_id: z.string(),
  role_id: z.string(),
  logo_url: z.string().nullable().optional(),
});

export type IntegrationData = z.infer<typeof IntegrationDataSchema>;

// ENTER Health /me endpoint response schema
const EnterHealthMeDataSchema = z
  .object({
    grantedAccountId: z.string().optional(),
    personalInfo: z
      .object({
        email: z.string().optional(),
        name: z.string().optional(),
      })
      .optional(),
    accountAccess: z
      .array(
        z.object({
          accountId: z.string(),
          name: z.string(),
          roleId: z.string(),
          logoUrl: z.string().optional(),
        })
      )
      .optional(),
  })
  .passthrough(); // Allow additional fields

export type EnterHealthMeData = z.infer<typeof EnterHealthMeDataSchema>;

// Integrations contract
export const integrationsContract = c.router({
  verifyIntegration: {
    method: 'POST',
    path: '/integrations/verify',
    body: z.object({
      apiKey: z.string().describe('ENTER Health API client ID'),
      secretKey: z.string().describe('ENTER Health API client secret'),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        organization: z.object({
          id: z.string(),
          name: z.string(),
          email: z.string().optional(),
        }),
        meData: EnterHealthMeDataSchema.describe('Full response from ENTER Health /me endpoint'),
        accessToken: z.string().describe('ENTER Health API access token'),
        refreshToken: z.string().optional().describe('ENTER Health API refresh token'),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Verify ENTER Health API credentials and retrieve organization information',
  },
  saveIntegration: {
    method: 'POST',
    path: '/integrations/save',
    body: SaveIntegrationSchema,
    responses: {
      200: z.object({
        success: z.boolean(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      409: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Save integration to WorkOS organization metadata',
  },
  getIntegrations: {
    method: 'GET',
    path: '/integrations',
    responses: {
      200: z.object({
        success: z.boolean(),
        integrations: z.array(IntegrationDataSchema),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get all integrations from WorkOS organization metadata',
  },
  deleteIntegration: {
    method: 'DELETE',
    path: '/integrations/:accountId',
    pathParams: z.object({
      accountId: z.string(),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Delete integration from WorkOS organization metadata',
  },
  refreshMcpToken: {
    method: 'POST',
    path: '/integrations/:accountId/refresh-token',
    pathParams: z.object({
      accountId: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: z.object({
        success: z.boolean(),
        accessToken: z.string().describe('Fresh ENTER Health API access token (15min expiry)'),
        refreshToken: z.string().optional().describe('ENTER Health API refresh token'),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Refresh access token for MCP integration using stored credentials',
  },
});

// Personalization contract
export const personalizationContract = c.router({
  getUserPersonalization: {
    method: 'GET',
    path: '/personalization/user',
    responses: {
      200: GetUserPersonalizationOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get user personalization settings',
  },
  updateUserPersonalization: {
    method: 'POST',
    path: '/personalization/user',
    body: UpdateUserPersonalizationInputSchema,
    responses: {
      200: UpdateUserPersonalizationOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Update user personalization settings',
  },
  getOrgPersonalization: {
    method: 'GET',
    path: '/personalization/org',
    responses: {
      200: GetOrgPersonalizationOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get organization personalization settings (admin only)',
  },
  updateOrgPersonalization: {
    method: 'POST',
    path: '/personalization/org',
    body: UpdateOrgPersonalizationInputSchema,
    responses: {
      200: UpdateOrgPersonalizationOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Update organization personalization settings (admin only)',
  },
  updateOrganizationName: {
    method: 'PATCH',
    path: '/personalization/org/name',
    body: z.object({
      name: z.string().min(1).max(100),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
        organization: z.object({
          id: z.string(),
          name: z.string(),
        }),
      }),
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      403: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Update organization name (admin only)',
  },
});

// Feedback contract
export const feedbackContract = c.router({
  getFeatures: {
    method: 'GET',
    path: '/feedback/features',
    responses: {
      200: GetFeaturesOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get all features and user feedback status',
  },
  submitFeedback: {
    method: 'POST',
    path: '/feedback/submit',
    body: SubmitFeedbackInputSchema,
    responses: {
      200: SubmitFeedbackOutputSchema,
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Submit user feedback for a feature',
  },
  dismissFeatures: {
    method: 'POST',
    path: '/feedback/dismiss',
    body: DismissFeaturesInputSchema,
    responses: {
      200: z.object({
        success: z.boolean(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Dismiss features (user clicked "Remind me later")',
  },
});

// Prompt Library contract - User-specific prompt management
export const promptLibraryContract = c.router({
  getUserPrompts: {
    method: 'GET',
    path: '/prompt-library/user/prompts',
    responses: {
      200: GetOrgPromptsOutputSchema, // Reuse same schema
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get user-specific prompt templates',
  },
  getUserPreferences: {
    method: 'GET',
    path: '/prompt-library/user/preferences',
    responses: {
      200: GetUserPreferencesOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Get user prompt preferences (favorites, usage, etc.)',
  },
  updateUserPreferences: {
    method: 'PATCH',
    path: '/prompt-library/user/preferences',
    body: UpdateUserPreferencesInputSchema,
    responses: {
      200: UpdateUserPreferencesOutputSchema,
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Update user prompt preferences',
  },
  createUserPrompt: {
    method: 'POST',
    path: '/prompt-library/user/prompts',
    body: CreatePromptInputSchema,
    responses: {
      201: CreatePromptOutputSchema,
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Create user-specific prompt',
  },
  updateUserPrompt: {
    method: 'PATCH',
    path: '/prompt-library/user/prompts/:promptId',
    body: UpdatePromptInputSchema,
    pathParams: z.object({
      promptId: z.string(),
    }),
    responses: {
      200: UpdatePromptOutputSchema,
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Update user-specific prompt',
  },
  deleteUserPrompt: {
    method: 'DELETE',
    path: '/prompt-library/user/prompts/:promptId',
    pathParams: z.object({
      promptId: z.string(),
    }),
    body: null,
    responses: {
      200: DeletePromptOutputSchema,
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      404: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Delete user-specific prompt',
  },
});

// OpenAI Text-to-Speech Contract
export const ttsContract = c.router({
  synthesize: {
    method: 'POST',
    path: '/tts/synthesize',
    body: TTSSynthesisRequestSchema,
    responses: {
      200: TTSSynthesisResponseSchema,
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Synthesize text to speech using OpenAI',
    description:
      'Convert text to speech using OpenAI TTS (defaults to tts-1 model with opus format for speed). Returns base64 encoded audio. HIPAA compliant - no data storage.',
  },
});

// OpenAI Speech-to-Text (Whisper) Contract
export const sttContract = c.router({
  transcribe: {
    method: 'POST',
    path: '/stt/transcribe',
    body: STTTranscriptionRequestSchema,
    responses: {
      200: STTTranscriptionResponseSchema,
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Transcribe audio to text using OpenAI Whisper',
    description:
      'Convert audio to text using OpenAI whisper-1 model. Accepts base64 encoded audio and returns transcribed text. HIPAA compliant - no data storage.',
  },
});

// Bug Report Contract
export const bugReportContract = c.router({
  submitBugReport: {
    method: 'POST',
    path: '/bug-report/submit',
    body: SubmitBugReportInputSchema,
    responses: {
      200: SubmitBugReportOutputSchema,
      400: z.object({
        error: z.string(),
        message: z.string(),
      }),
      401: z.object({
        error: z.string(),
        message: z.string(),
      }),
      500: z.object({
        error: z.string(),
        message: z.string(),
      }),
    },
    summary: 'Submit bug report with comprehensive diagnostic data',
    description:
      'Submit a bug report with maximum data collection including system info, console logs, screenshots, and app context. Sent to Sentry for tracking and analysis.',
  },
});

// AI Skills contract — returns wire-safe skill list (no systemPrompt / outputFormatPrompt)
export const aiSkillsContract = c.router({
  listSkills: {
    method: 'GET',
    path: '/ai-skills',
    responses: {
      200: z.object({
        skills: z.array(AiSkillPublicSchema),
      }),
      401: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'List available AI specialist skills (UX fields only)',
  },
  updateSessionSkill: {
    method: 'PUT',
    path: '/chat-history/:sessionId/skill',
    pathParams: z.object({ sessionId: z.string() }),
    body: z.object({
      aiSkillId: z.string().nullable().describe('Skill ID to set, or null to clear'),
    }),
    responses: {
      200: z.object({ success: z.boolean(), aiSkillId: z.string().nullable() }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Set or clear the active AI skill for a chat session',
  },
});

// Real-Time Mode Contract (EMR integration → Enter patient resolution)
export const realTimeModeContract = c.router({
  resolve: {
    method: 'POST',
    path: '/real-time-mode/resolve',
    body: ResolveRealTimeModeInputSchema,
    responses: {
      200: ResolveRealTimeModeOutputSchema,
      400: z.object({ error: z.string(), message: z.string() }),
      401: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Resolve an EMR-observed patient context to Enter Real-Time Mode data',
    description:
      'Accepts ObservedContext extracted by a browser integration provider and returns the full match-state machine plus, when matched, the Real-Time Mode data (balance, claims, issues, recommended actions).',
  },
});

// Enter Tasks Contract — the current user's Enter RCM worklist (open messages).
// See docs/ENTER_TASKS_INTEGRATION.md for the verified Enter API mapping.
export const paymentsContract = c.router({
  record: {
    method: 'POST',
    path: '/payments',
    body: RecordPaymentInputSchema,
    responses: {
      200: z.object({ payment: RecordedPaymentSchema }),
      400: z.object({ error: z.string(), message: z.string() }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: "Record a manual patient payment against an Enter claim",
  },
  list: {
    method: 'GET',
    path: '/payments',
    query: z.object({
      accountId: z.string(),
      claimIds: z.string().describe("Comma-separated claim ids to list payments for."),
    }),
    responses: {
      200: z.object({ payments: z.array(PaymentListItemSchema) }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: "List existing payments on a patient's claims (for the refund picker)",
  },
  refund: {
    method: 'POST',
    path: '/payments/:id/refund',
    pathParams: z.object({ id: z.string() }),
    body: RefundPaymentInputSchema,
    responses: {
      200: z.object({ payment: RecordedPaymentSchema }),
      400: z.object({ error: z.string(), message: z.string() }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Refund (part of) an existing patient payment',
  },
});

export const tasksContract = c.router({
  list: {
    method: 'GET',
    path: '/tasks',
    query: z.object({
      scope: z
        .enum(['my', 'all'])
        .optional()
        .describe("'my' (default) = the caller's tasks; 'all' = the whole team's."),
      accountId: z
        .string()
        .optional()
        .describe(
          'Enter account (practice) to scope to — the one selected in the account picker. ' +
            'Omitted means every connected practice, which is almost never what a user wants: ' +
            "a biller working one clinic should not see another clinic's worklist."
        ),
    }),
    responses: {
      200: z.object({
        tasks: z.array(EnterTaskSchema),
        integration: z.enum(['connected', 'none']),
      }),
      401: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: "List the current user's open Enter tasks (RCM worklist)",
  },
  listForClaims: {
    method: 'GET',
    path: '/tasks/for-claims',
    query: z.object({
      claimIds: z
        .string()
        .describe("Comma-separated Enter claim ids (the patient's claims from the RTM snapshot)."),
      accountId: z.string().optional().describe('Enter account to scope to — see tasks.list.'),
    }),
    responses: {
      200: z.object({ tasks: z.array(EnterTaskSchema) }),
      401: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: "List open Enter tasks for a patient's claims (RTM patient snapshot)",
  },
  close: {
    method: 'POST',
    path: '/tasks/:id/close',
    pathParams: z.object({ id: z.string() }),
    body: CloseTaskInputSchema,
    responses: {
      200: z.object({ success: z.boolean() }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Close an Enter task (marks the underlying Enter message closed)',
  },
  claimSummary: {
    method: 'GET',
    path: '/tasks/claim-summary',
    query: z.object({ accountId: z.string(), claimId: z.string() }),
    responses: {
      200: z.object({ claim: ClaimSummarySchema.nullable() }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary:
      "Patient identity for a task's claim — name, DOB and MRN (patientExternalId), plus DOS/payer/charge. One upstream call; used to let a biller find the record in their EMR.",
  },
  comments: {
    method: 'GET',
    path: '/tasks/:id/comments',
    pathParams: z.object({ id: z.string() }),
    query: z.object({ accountId: z.string() }),
    responses: {
      200: z.object({ comments: z.array(TaskCommentSchema) }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary:
      "Read a task's comments. Used to recover which resolve action already ran, so the Resolved state survives a reload.",
  },
  comment: {
    method: 'POST',
    path: '/tasks/:id/comment',
    pathParams: z.object({ id: z.string() }),
    body: PostTaskCommentInputSchema,
    responses: {
      200: z.object({ success: z.boolean() }),
      400: z.object({ error: z.string(), message: z.string() }),
      401: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary:
      'Post a comment on an Enter task. This is also how the Resolve actions work — Enter has no dedicated resolve API; a sentinel comment body (e.g. WAITING_FOR_EMR_UPDATE) is what its backend acts on.',
  },
});

// ─── Admin contract ────────────────────────────────────────────────────────

const OrgMemberSchema = z.object({
  membershipId: z.string(),
  userId: z.string(),
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  profilePictureUrl: z.string().optional(),
  role: z.enum(['admin', 'member']),
  status: z.enum(['active', 'pending', 'inactive']),
  joinedAt: z.string(),
  // WorkOS User fields (surfaced from getUser but previously dropped)
  lastSignInAt: z.string().optional(),
  emailVerified: z.boolean().optional(),
  accountCreatedAt: z.string().optional(),
});

const PendingInvitationSchema = z.object({
  invitationId: z.string(),
  email: z.string(),
  role: z.enum(['admin', 'member']),
  expiresAt: z.string().optional(),
  sentAt: z.string(),
  state: z.enum(['pending', 'accepted', 'expired', 'revoked']).default('pending'),
  // WorkOS-hosted accept URL — lets the admin copy a direct invite link.
  acceptInvitationUrl: z.string().optional(),
});

export const adminContract = c.router({
  listMembers: {
    method: 'GET',
    path: '/admin/team/members',
    responses: {
      200: z.object({
        members: z.array(OrgMemberSchema),
        invitations: z.array(PendingInvitationSchema),
        total: z.number(),
      }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'List all org members and pending invitations',
  },
  removeMember: {
    method: 'DELETE',
    path: '/admin/team/members/:membershipId',
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean(), seatCount: z.number() }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Remove a member from the org and sync Stripe seat count',
  },
  updateMemberRole: {
    method: 'PATCH',
    path: '/admin/team/members/:membershipId/role',
    body: z.object({ role: z.enum(['admin', 'member']) }),
    responses: {
      200: z.object({ success: z.boolean() }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Update a member role (admin ↔ member)',
  },
  inviteMember: {
    method: 'POST',
    path: '/admin/team/members/invite',
    body: z.object({
      email: z.string().email(),
      role: z.enum(['admin', 'member']).optional().default('member'),
    }),
    responses: {
      200: z.object({ success: z.boolean(), invitationId: z.string().optional() }),
      400: z.object({ error: z.string(), message: z.string() }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      409: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Invite a new user to the org via email',
  },
  revokeInvitation: {
    method: 'DELETE',
    path: '/admin/team/invitations/:invitationId',
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean() }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Revoke a pending invitation',
  },
  resendInvitation: {
    method: 'POST',
    path: '/admin/team/invitations/:invitationId/resend',
    body: z.object({ email: z.string().email() }),
    responses: {
      200: z.object({ success: z.boolean(), invitationId: z.string().optional() }),
      400: z.object({ error: z.string(), message: z.string() }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Resend a pending invitation (revoke the stale one + send a fresh email)',
  },
  getUsage: {
    method: 'GET',
    path: '/admin/usage',
    responses: {
      200: z.object({
        users: z.array(
          z.object({
            userId: z.string(),
            email: z.string(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            messages: z.number(),
            sessions: z.number(),
            files: z.number(),
            voiceSeconds: z.number(),
            lastActive: z.string().optional(),
            lastSignInAt: z.string().optional(),
            emailVerified: z.boolean().optional(),
            accountCreatedAt: z.string().optional(),
          })
        ),
        totals: z.object({
          messages: z.number(),
          sessions: z.number(),
          files: z.number(),
          voiceSeconds: z.number(),
        }),
      }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Get per-user usage statistics for the organization',
  },
  getUsageTimeseries: {
    method: 'GET',
    path: '/admin/usage/timeseries',
    query: z.object({
      days: z.coerce.number().int().min(1).max(90).default(30),
      endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    }),
    responses: {
      200: z.object({
        range: z.object({
          start: z.string(),
          end: z.string(),
          days: z.number(),
        }),
        series: z.array(
          z.object({
            date: z.string(),
            totals: z.object({
              messages: z.number(),
              sessions: z.number(),
              files: z.number(),
            }),
            perUser: z.array(
              z.object({
                userId: z.string(),
                messages: z.number(),
                sessions: z.number(),
                files: z.number(),
              })
            ),
          })
        ),
        users: z.array(
          z.object({
            userId: z.string(),
            email: z.string(),
            firstName: z.string().optional(),
            lastName: z.string().optional(),
          })
        ),
      }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Get per-user daily usage time-series for the organization',
  },
  getUserDetail: {
    method: 'GET',
    path: '/admin/usage/user/:userId',
    query: z.object({
      days: z.coerce.number().int().min(1).max(90).default(30),
      endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    }),
    responses: {
      200: z.object({
        profile: z.object({
          userId: z.string(),
          email: z.string(),
          firstName: z.string().optional(),
          lastName: z.string().optional(),
          profilePictureUrl: z.string().optional(),
          role: z.enum(['admin', 'member']),
          status: z.enum(['active', 'pending', 'inactive']),
          joinedAt: z.string(),
          lastSignInAt: z.string().optional(),
          emailVerified: z.boolean().optional(),
          accountCreatedAt: z.string().optional(),
          locale: z.string().optional(),
        }),
        lifetime: z.object({
          messages: z.number(),
          sessions: z.number(),
          files: z.number(),
          lastActive: z.string().optional(),
        }),
        daily: z.object({
          range: z.object({ start: z.string(), end: z.string(), days: z.number() }),
          series: z.array(z.object({
            date: z.string(),
            messages: z.number(),
            sessions: z.number(),
            files: z.number(),
          })),
        }),
        chat: z.object({
          totalSessions: z.number(),
          archivedSessions: z.number(),
          avgMessagesPerSession: z.number(),
          totalUserMessages: z.number(),
          totalAiMessages: z.number(),
          pageContextCount: z.number(),
          specialists: z.array(z.object({
            skillId: z.string(),
            name: z.string(),
            sessions: z.number(),
            messages: z.number(),
          })),
          hourly: z.array(z.number()).length(24),
          dayOfWeek: z.array(z.number()).length(7),
          /** Per-day hourly breakdown for 2D month heatmap (date YYYY-MM-DD, hours[24] UTC). */
          hourlyByDay: z.array(z.object({
            date: z.string(),
            hours: z.array(z.number()).length(24),
          })),
          /** Per-week day-of-week breakdown for 2D weekly heatmap (weekStart Sunday UTC, days[7] Sun–Sat). */
          weeklyActivity: z.array(z.object({
            weekStart: z.string(),
            days: z.array(z.number()).length(7),
          })),
          files: z.object({
            total: z.number(),
            totalSizeMb: z.number(),
            byType: z.array(z.object({ contentType: z.string(), count: z.number() })),
          }),
          recentSessions: z.array(z.object({
            sessionId: z.string(),
            title: z.string(),
            createdAt: z.string(),
            updatedAt: z.string(),
            messageCount: z.number(),
            aiSkillId: z.string().optional(),
            aiSkillName: z.string().optional(),
            isArchived: z.boolean(),
          })),
          sessionsAnalyzed: z.number(),
          sessionsTotal: z.number(),
          truncated: z.boolean(),
        }),
      }),
      401: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Get full per-user detail stats for the admin dashboard modal',
  },
});

// ─── Internal (Ops Console / manual-billing issuer) ──────────────────────────
// Enter-staff only, gated by withInternalAdmin. Issues WorkOS invitations (email → accept
// link). See docs/INVITE-INVOICE-BILLING-FLOW.md §0.
const InviteRecordSchema = z.object({
  organizationId: z.string(),
  orgName: z.string(),
  seatCount: z.number(),
  status: z.enum(['unredeemed', 'redeemed', 'revoked']),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string(),
  // Invitation flow
  email: z.string().optional(),
  invitationId: z.string().optional(),
  acceptInvitationUrl: z.string().optional(),
  invitationState: z.enum(['pending', 'accepted', 'expired', 'revoked']).optional(),
  manualStatus: z.enum(['active', 'suspended']).optional(),
});

export const internalContract = c.router({
  // Cheap authorization probe — 200 only if the caller is an allowlisted internal admin
  // (withInternalAdmin returns 403 otherwise). Lets the Ops Console route decide what to render.
  me: {
    method: 'GET',
    path: '/internal/me',
    responses: {
      200: z.object({ authorized: z.boolean() }),
      403: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Whether the caller is an authorized internal admin',
  },
  issueInvite: {
    method: 'POST',
    path: '/internal/invites',
    body: z.object({
      orgName: z.string().min(1),
      seats: z.number().int().min(1),
      email: z.string().email(),
      expiresAt: z.string().nullable().optional(),
      createdBy: z.string().optional(),
    }),
    responses: {
      200: z.object({
        organizationId: z.string(),
        orgName: z.string(),
        seatCount: z.number(),
        email: z.string(),
        invitationId: z.string(),
        acceptInvitationUrl: z.string(),
      }),
      400: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      // Email already belongs to an org (would create a multi-org / Stripe collision).
      409: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Pre-create a manual-billing org and send an admin invitation to the customer',
  },
  listInvites: {
    method: 'GET',
    path: '/internal/invites',
    responses: {
      200: z.object({ invites: z.array(InviteRecordSchema) }),
      403: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'List issued invite codes / manual orgs',
  },
  resendInvite: {
    method: 'POST',
    path: '/internal/orgs/:orgId/resend-invite',
    body: z.object({}),
    responses: {
      200: z.object({
        success: z.boolean(),
        invitationId: z.string(),
        acceptInvitationUrl: z.string(),
        email: z.string(),
      }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Revoke the stale invitation and send a fresh admin invite to the same email',
  },
  setSeatCap: {
    method: 'PATCH',
    path: '/internal/orgs/:orgId/seat-cap',
    body: z.object({ seats: z.number().int().min(1) }),
    responses: {
      200: z.object({ success: z.boolean(), seatCount: z.number() }),
      400: z.object({ error: z.string(), message: z.string() }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Raise or lower the seat cap for a manual org',
  },
  suspendOrg: {
    method: 'POST',
    path: '/internal/orgs/:orgId/suspend',
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean(), status: z.enum(['active', 'suspended']) }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Suspend a manual org (access gate closes on next check)',
  },
  resumeOrg: {
    method: 'POST',
    path: '/internal/orgs/:orgId/resume',
    body: z.object({}),
    responses: {
      200: z.object({ success: z.boolean(), status: z.enum(['active', 'suspended']) }),
      403: z.object({ error: z.string(), message: z.string() }),
      404: z.object({ error: z.string(), message: z.string() }),
      500: z.object({ error: z.string(), message: z.string() }),
    },
    summary: 'Resume a suspended manual org',
  },
});

// Main API contract combining all routes
export const apiContract = c.router({
  health: healthContract,
  auth: authContract,
  ai: aiContract,
  fileUpload: fileUploadContract,
  billing: billingContract,
  plans: plansContract,
  pandadoc: pandaDocContract,
  webhook: webhookContract,
  chatHistory: chatHistoryContract,
  integrations: integrationsContract,
  personalization: personalizationContract,
  feedback: feedbackContract,
  promptLibrary: promptLibraryContract,
  tts: ttsContract,
  stt: sttContract,
  bugReport: bugReportContract,
  aiSkills: aiSkillsContract,
  realTimeMode: realTimeModeContract,
  tasks: tasksContract,
  payments: paymentsContract,
  admin: adminContract,
  internal: internalContract,
});

export type ApiContract = typeof apiContract;
